package com.vamvisions.main.portfolio.presentation.projects

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.vamvisions.main.core.domain.Result
import com.vamvisions.main.core.domain.preferences.Language
import com.vamvisions.main.core.presentation.i18n.toUiText
import com.vamvisions.main.core.presentation.navigation.Route
import com.vamvisions.main.portfolio.domain.model.Project
import com.vamvisions.main.portfolio.domain.model.ProjectStatus
import com.vamvisions.main.portfolio.domain.repository.ProjectRepository
import com.vamvisions.main.portfolio.presentation.common.LanguageLoader
import com.vamvisions.main.portfolio.presentation.common.toSkillUi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

/** Loads the projects once the page reports its language, and again on every change. */
class ProjectsViewModel(
    private val projectRepository: ProjectRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(ProjectsState())
    val state = _state.asStateFlow()

    private val loader = LanguageLoader(
        scope = viewModelScope,
        fetch = projectRepository::get,
        onStart = { _state.update { it.copy(isLoading = true, error = null) } },
        onResult = { language, result ->
            _state.update {
                when (result) {
                    is Result.Success -> {
                        val (featured, earlier) = result.data.partition { p -> p.featured }
                        ProjectsState(
                            isLoading = false,
                            featured = featured.map { p -> p.toProjectUi(language) },
                            earlier = earlier.map { p -> p.toProjectUi(language) },
                        )
                    }
                    is Result.Error -> ProjectsState(isLoading = false, error = result.error.toUiText())
                }
            }
        },
    )

    fun onAction(action: ProjectsAction) {
        when (action) {
            ProjectsAction.Retry -> loader.retry()
            is ProjectsAction.LanguageChanged -> loader.languageChanged(action.language)
        }
    }

    private fun Project.toProjectUi(language: Language) = ProjectUi(
        slug = slug,
        name = name,
        description = description.ifBlank { null },
        meta = meta?.takeIf { it.isNotBlank() } ?: year.toString(),
        comingSoon = status == ProjectStatus.COMING_SOON,
        iconUrl = iconUrl,
        skills = skills.map { it.toSkillUi() },
        policyRoute = privacyPolicySlug?.let { Route.ProjectDocument(it, language) },
        storeLinks = storeLinks.map { StoreLinkUi(it.store, it.url) },
        websiteUrl = websiteUrl,
        sourceUrl = sourceUrl,
    )
}
